import { useEffect, useMemo, useState } from 'react'

import { previewBillingCycle, runBillingCycle, watchBillingRuns } from '../lib/data.js'
import { fmtDate, fmtDateTime, fmtMoney } from '../lib/format.js'

export default function Billing() {
  const [runs, setRuns] = useState({})
  useEffect(() => watchBillingRuns(setRuns), [])

  const history = useMemo(() => Object.entries(runs)
    .map(([id, v]) => ({ id, ...v }))
    .sort((a, b) => (b.runAt || 0) - (a.runAt || 0))
  , [runs])

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-extrabold">Billing</h1>

      <div className="grid sm:grid-cols-2 gap-4">
        <CyclePanel kind="1st" />
        <CyclePanel kind="15th" />
      </div>

      <div className="card">
        <h2 className="font-extrabold">Recent billing runs</h2>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500 mt-3">No runs yet. Use the buttons above to start one.</p>
        ) : (
          <table className="w-full text-sm mt-3">
            <thead className="text-xs uppercase tracking-widest text-gray-500 border-b">
              <tr>
                <th className="text-left py-2">Run at</th>
                <th className="text-left py-2">Cycle</th>
                <th className="text-left py-2">Period</th>
                <th className="text-left py-2">Due</th>
                <th className="text-right py-2">Invoices</th>
                <th className="text-right py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {history.map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-2">{fmtDateTime(r.runAt)}</td>
                  <td className="py-2 font-bold">{r.kind}</td>
                  <td className="py-2">{r.period}</td>
                  <td className="py-2">{fmtDate(r.dueDate)}</td>
                  <td className="py-2 text-right font-bold">{r.invoicesCreated}</td>
                  <td className="py-2 text-right font-bold">{fmtMoney(r.totalAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function CyclePanel({ kind }) {
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState(null)

  const loadPreview = async () => {
    setBusy(true); setFlash(null)
    try {
      const p = await previewBillingCycle(kind)
      setPreview(p)
    } catch (e) {
      setFlash({ kind: 'err', msg: e?.message || 'Preview failed.' })
    } finally {
      setBusy(false)
    }
  }

  const run = async () => {
    if (!preview || preview.count === 0) return
    if (!confirm(`Run ${kind} cycle? ${preview.count} invoice${preview.count === 1 ? '' : 's'} totaling ${fmtMoney(preview.total)} will be generated for ${preview.period}.`)) return
    setBusy(true); setFlash(null)
    try {
      const res = await runBillingCycle(kind)
      setFlash({ kind: 'ok', msg: `✓ Generated ${res.invoicesCreated} invoice${res.invoicesCreated === 1 ? '' : 's'} for ${fmtMoney(res.totalAmount)}.` })
      setPreview(null)
    } catch (e) {
      setFlash({ kind: 'err', msg: e?.message || 'Run failed.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <h2 className="font-extrabold">Run {kind} Cycle</h2>
      <p className="text-xs text-gray-500 mt-1">
        Generates one invoice for every active customer on the {kind} of the month.
      </p>

      {flash ? (
        <div className={`rounded-lg border px-3 py-2 text-sm mt-3 ${flash.kind === 'ok' ? 'bg-brand-mint border-brand-green text-brand-green-deep' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {flash.msg}
        </div>
      ) : null}

      {preview ? (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="font-bold text-amber-900">
            {preview.count} invoice{preview.count === 1 ? '' : 's'} totaling <span className="text-brand-green-deep">{fmtMoney(preview.total)}</span> for {preview.count === 1 ? '1 customer' : `${preview.count} customers`}.
          </p>
          <p className="text-xs text-amber-800 mt-0.5">Due {fmtDate(preview.dueDate)} · {preview.period}</p>
          <ul className="text-xs text-gray-700 mt-2 max-h-40 overflow-y-auto">
            {preview.customers.map((c) => (
              <li key={c.id} className="flex justify-between gap-2 py-0.5">
                <span className="truncate">{c.name}</span>
                <span className="font-bold">{fmtMoney(c.monthlyAmount)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex gap-2 mt-3">
        <button className="btn-secondary" onClick={loadPreview} disabled={busy}>Preview</button>
        <button className="btn-gold" onClick={run} disabled={busy || !preview || preview.count === 0}>
          {busy ? 'Running…' : `Run ${kind} cycle`}
        </button>
      </div>
    </div>
  )
}
